import React, {
    FC, createContext, useContext, useState, ReactNode, MouseEvent, useEffect, useRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
// import { push } from 'connected-react-router'
import {
    PageHeader, Space, Table, Form, Select, Checkbox, Button, Input, Typography, Popover,
    ConfigProvider
} from 'antd'
import {
    DownOutlined, SearchOutlined, FormOutlined, CloudDownloadOutlined,
    FileSearchOutlined, PlusOutlined, NodeExpandOutlined, ClockCircleOutlined,
    PlusCircleOutlined, NodeIndexOutlined, DiffOutlined, CodepenOutlined, MergeCellsOutlined
} from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import type { TableProps, InputRef, PaginationProps } from 'antd'
import zhCN from 'antd/es/locale/zh_CN'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
import DSlate, { DefaultPlugin } from '@dslate/dslate'
import type { FormInstance } from 'antd/es/form'
import type { ColumnType } from 'antd/es/table'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import type { FilterValue, FilterConfirmProps } from 'antd/es/table/interface'
// import type { CheckboxValueType } from 'antd/es/checkbox/Group'
import type { IProjectInfo, IClassification } from '@/store/models/tableEdit.model'
import {
    setEdit2Data, clearEdit2Data, getProjectInfoList, setClassificationPagination,
    getWorkgroup, setProjectInfoPagination, setTechClassification,
} from '@/store/actions/tableEdit.action'
import { dataSource2 } from '@/helpers/tableData'
import { IAppState } from '@/store/reducers'
import Table2SearchPop from '@/pages/table2Mana/components/table2SearchPop'
import LogListPop from '@/pages/table2Mana/components/logListPop'
import HistoryListPop from '@/pages/table2Mana/components/historyListPop'
import TechGraphPop from '@/pages/table2Mana/components/techGraphPop'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import { getDicClass } from '@/pages/charts'
import { hasClass, getUuid, downloadFile, autoClick } from '@/utils/util'
import { exportExcel, getTechClassificationListAll } from '@/services/table.service'
import { getBUList, getProdList } from '@/services/progress.service'
import ResizableTable from '@/components/resizableTable'
import Nav from '@/components/nav'

interface EditableRowProps {
    index: number
}
interface EditableCellProps {
    title: ReactNode
    editable: boolean
    children: ReactNode
    dataIndex: string
    record: any
}
interface childrenType {
    title: string
    dataIndex: string
    key: string
    editable?: boolean
    className?: string
    width?: number
    ellipsis: { showTitle: boolean },
    filters?: {
        text: string
        value: number
    }[]
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onFilter?: (value: string, record: any) => boolean
    filteredValue?: FilterValue | null
    render?: any
}
interface IColProperty {
    title?: ReactNode | string
    className?: string
    editable?: boolean
    dataIndex?: string
    children?: childrenType[]
    filters?: {
        text: string
        value: string
    }[]
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onFilter?: (value: string | number | boolean, record: any) => boolean
}
type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>
type DataIndex = keyof IProjectInfo;

const plugs = { ...DefaultPlugin }
// 删除图片工具
// const plugsLen = Object.keys(plugs).length
// let plugsIndex = 0
for (const key in plugs) {
    if (key === 'HistoryPlugin') continue
    Reflect.deleteProperty(plugs, key)
}


export const wrapText = (text: string): ReactNode => {
    const br = <br />
    const empty = <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
    let result
    const contentStr = text ? text.split(';') : ''
    if (contentStr.length < 2) {
        return text
    }
    for (let j = 0; j < contentStr.length; j++) {
        if (j == 0) {
            result = contentStr[j]
        } else if (contentStr[j].startsWith(' ')) {
            result = <span>{result}{br}{empty}{contentStr[j]}</span>
        } else {
            result = <span>{result}{br}{contentStr[j]}</span>
        }
    }
    return <div>{result}</div>
}

const EditableContext = createContext<FormInstance<any> | null>(null)
const DataSourceContext = createContext<{
    dataSource: IProjectInfo[]
    setDataSource: any
} | null>(null)
export const DrawerShowContext = createContext<{
    showSearchPop: boolean
    setShowSearchPop: any
    searchObj: any
    setSearchObj: any
    classArr: any[]
    prodList: any[]
    buList: any[]
    curWorkgroup: {
        id: number
        groupName: string
    } | undefined
}>({
    showSearchPop: false,
    setShowSearchPop: null,
    searchObj: null,
    setSearchObj: null,
    classArr: [],
    prodList: [],
    buList: [],
    curWorkgroup: undefined
})

const EditableRow: FC<EditableRowProps> = ({ ...props }) => {
    const [form] = Form.useForm()
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    )
}
const EditableCell: FC<EditableCellProps> = ({
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    title,
    editable,
    children,
    dataIndex,
    record,
    ...restProps
}) => {
    const { Option } = Select
    const [editing, setEditing] = useState(false)
    const form = useContext(EditableContext)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { dataSource, setDataSource } = useContext(DataSourceContext)!

    const toggleEdit = (value?: string | MouseEvent<HTMLDivElement>): void => {
        if (!record) return
        setEditing(!editing)
        // 修改 form 值为 value
        form?.setFieldsValue({ [dataIndex]: value || record[dataIndex] })
    }

    const saveChange = async (e: string) => {
        const newData = [...dataSource]
        const findIndex = newData.findIndex(item => ('key' in item ? item.key === record.key : item.id === record.id))
        if (findIndex !== -1) {
            const item = newData[findIndex]
            // 修改原 dataSource
            newData.splice(findIndex, 1, {
                ...item,
                [dataIndex]: e ? e.toUpperCase() : ''
            })
            setDataSource(newData)
        }
        try {
            toggleEdit(e)
        } catch (errInfo: any) {
            console.log('Save failed:', errInfo.message)
        }
    }
    const save = () => {
        try {
            toggleEdit()
        } catch (errInfo: any) {
            console.log('Save failed:', errInfo.message)
        }
    }
    const saveStop = (e: MouseEvent<HTMLDivElement>): void => {
        e.stopPropagation()
    }

    let childNode = children

    if (editable) {
        const bg = record[dataIndex] === 'ON' ? 'on_bg' : (record[dataIndex] === 'PLAN' ? 'plan_bg' : 'empty_bg')
        childNode = editing ? (
            <Form.Item className={`table_input ${bg}`}
                style={{ margin: 0 }}
                name={dataIndex}
                initialValue={{ [dataIndex]: 'on' }}
            >
                <Select style={{ width: '100%' }}
                    onChange={saveChange}
                    onBlur={save}
                    onClick={saveStop}
                    allowClear
                >
                    <Option value="on">ON</Option>
                    <Option value="plan">PLAN</Option>
                </Select>
            </Form.Item>
        ) : (
            <div className={`editable-cell-value-wrap ${bg}`}
                style={{ paddingRight: 24 }}
                onClick={toggleEdit}
            >
                {children}
            </div>
        )
    }

    return <td {...restProps} onClick={save}>{childNode}</td>
}

const TablePage2: FC = () => {
    const dispatch = useDispatch()
    const history: any = useHistory()
    const locationObj = useLocation<any>()
    const { t, i18n } = useTranslation()
    const route = useSelector((state: IAppState) => state.router)
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    // const classification = useSelector((state: IAppState) => state.tableEditData.techClassification)
    const projectInfo = useSelector((state: IAppState) => state.tableEditData.projectInfo)
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)
    const paginationInfo = useSelector((state: IAppState) => state.tableEditData.projectInfoPagination)

    const { Text } = Typography
    const { Option } = Select

    const [init, setInit] = useState(true)
    const [classArr, setClassArr] = useState<any[]>([])
    const [dataSource, setDataSource] = useState<IProjectInfo[]>(dataSource2)
    // const [dataTotal, setDataTotal] = useState(0)
    const [winH, setWinh] = useState<number>(0)
    const [columns, setColumns] = useState<any[]>([])
    const [classification, setClassification] = useState<IClassification[]>([])
    const [prodList, setProdList] = useState<any[]>([])
    const [buList, setBuList] = useState<any[]>([])
    // 是否已获取过 dicClassList
    const [allCheck, setAllCheck] = useState<boolean>(true)
    // 所有列
    const [dataIndexs, setDataIndexs] = useState<string[]>([])
    // 选择可显示列下拉列表
    const [filterOptions, setFilterOptions] = useState<any[]>([])
    // 保存选择的列
    const [filterArr, setFilterArr] = useState<string[]>([])
    // 列内容搜索
    const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({})
    // 列自定义搜索
    const [searchText, setSearchText] = useState('')
    // 自定义搜索
    const [searchedColumn, setSearchedColumn] = useState<DataIndex>('')
    const [curWorkgroup, setCurWorkgroup] = useState<{
        id: number
        groupName: string
    } | undefined>(undefined)
    const [tableLoading, setTableLoading] = useState(true)

    const searchInput = useRef<InputRef>(null)
    const allFilter = useRef<any[]>([])
    const locationSearch = useRef<any>({})
    // pageSize 变更后，会重复触发 pageChange 事件
    const pageSizeChanged = useRef<boolean>(false)
    // 高级搜索
    const [showSearchPop, setShowSearchPop] = useState(false)
    const [searchObj, setSearchObj] = useState<any>({})
    const [tableKey, setTableKey] = useState('0')
    // 表格搜索条件, 列标题上的搜索
    // const searchColumnCont = useRef<{
    //     value: string | number | boolean
    //     fieldname: string
    //     record: any
    // }>({
    //     value: '',
    //     fieldname: '',
    //     record: null
    // })
    // 表格渲染当前分类 id , 同一类只有第一条设置 rowspan , 后面的全部设为 0 
    // const rowClassObj = useRef<{
    //     [id: string]: number
    // } | null>(null)
    const [viewLogId, setViewLogId] = useState<number | null>(null)
    const [viewHistoryId, setViewHistoryId] = useState<number | null>(null)
    const [viewTechGraphId, setViewTechGraphId] = useState<number | null>(null)
    const [projectIdParam, setProjectIdParam] = useState('')

    const setEmptyData = () => {
        dispatch(setEdit2Data({
            id: -1,
            version: 0,
            buStakeholder: '',
            description: '',
            developedBy: '',
            techLevel: '',
            maturity: '',
            priority: '',
            quantitativeIndicators: '',
            requirementSource: '',
            researchForms: '',
            resourceNeeded: '',
            subsidiary: '',
            techClassId: '',
            isMajorProject: undefined,
            classificationName: '',
            techName: '',
            techPtn: '',
            tmtapproval: '',
            toolsEquipment: '',
            techYearQuarters: []
        }))
    }

    const viewButton = (_: string, record: any) => {
        return authInfo ? (
            <Space wrap>
                {/* 编辑 */}
                {Reflect.has(authInfo, 'editProjectInfo') && <Text className='txt_blue hand' title={t('edit')}
                    onClick={() => {
                        setEmptyData()
                        history.push('/projectInfoEdit?workGroupId=' + locationSearch.current.workGroupId + '&projectId=' + record.id)
                    }}
                ><FormOutlined style={{ fontSize: 16 }} /></Text>}
                {/* 添加修改申请 */}
                {Reflect.has(authInfo, 'changeApply') && <Text className='txt_blue hand' title={t('editApply')}
                    onClick={() => {
                        history.push('/projectInfoChangeApply?workGroupId=' + locationSearch.current.workGroupId + '&projectId=' + record.id)
                    }}
                ><DiffOutlined style={{ fontSize: 16 }} /></Text>}
                {/* 审核修改申请 */}
                {/* <Text className='txt_blue hand' title={t('approvalEdit')}
                    onClick={() => {
                        setEmptyData()
                        history.push('/projectInfoChangeAppr?workGroupId=' + locationSearch.current.workGroupId + '&projectId=' + record.id)
                    }}
                ><IssuesCloseOutlined style={{ fontSize: 16 }} /></Text> */}
                {Reflect.has(authInfo, 'addSubTech') && <Text className='txt_blue hand' title={t('addSubTech')}
                    onClick={() => {
                        // 清空表单数据
                        dispatch(clearEdit2Data())
                        history.push('/projectInfoAdd?workGroupId=' + locationSearch.current.workGroupId + '&parentId=' + record.id)
                    }}
                ><PlusCircleOutlined style={{ fontSize: 16 }} /></Text>}
                {/* 课题状态变更申请 */}
                {Reflect.has(authInfo, 'techChangeList') && <Text className='txt_blue hand' title={t('Tech Change Status') + (i18n.language !== 'zh' ? ' ' : '') + t('apply')}
                    onClick={() => {
                        history.push('/techChangeStatus?recordid=' + record.id)
                    }}
                ><CodepenOutlined style={{ fontSize: 18 }} /></Text>}
                {Reflect.has(authInfo, 'viewDetailProjectInfo') && <a target='_blank' rel='noopener'
                    href={`/projectInfoView?workGroupId=${locationSearch.current.workGroupId}&projectId=${record.id}&isView=true`}
                ><Text className='txt_blue hand' title={t('view')}
                        onClick={() => {
                        // setEmptyData()
                        // history.push('/projectInfoView?workGroupId=' + locationSearch.current.workGroupId + '&projectId=' + record.id + '&isView=true')
                        // autoClick(`/projectInfoView?workGroupId=${locationSearch.current.workGroupId}&projectId=${record.id}&isView=true`)
                        }}
                    ><FileSearchOutlined style={{ fontSize: 16 }} /></Text></a>}
                {Reflect.has(authInfo, 'viewLogProjectInfo') && <Text className='txt_blue hand' title={t('viewLog')}
                    onClick={() => {
                        setViewLogId(record.id)
                    }}
                ><ClockCircleOutlined style={{ fontSize: 16 }} /></Text>}
                {Reflect.has(authInfo, 'viewHistoryProjectInfo') && <Text className='txt_blue hand' title={t('viewHistory')}
                    onClick={() => {
                        setViewHistoryId(record.id)
                    }}
                ><NodeIndexOutlined style={{ fontSize: 16 }} /></Text>}
                <Text className='txt_blue hand' title={t('techGraph')}
                    onClick={() => {
                        setViewTechGraphId(record.id)
                    }}
                ><MergeCellsOutlined style={{ fontSize: 16 }} /></Text>
            </Space>
        ) : <></>
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const setSourceRows = (): void => {
        const classArr: {
            [propertys: number]: number
        } = {}
        const _projectInfo = [...projectInfo]

        _projectInfo.forEach(project => {
            if (!(project.techClassId in classArr)) {
                classArr[project.techClassId as number] = 1
            } else {
                classArr[project.techClassId as number]++
            }
        })
        // 先按分类排序，使相同类别数据挨着
        _projectInfo.sort((a, b) => (a.techClassId as number) - (b.techClassId as number))
        // 原意在此按分类把第一条设好 rowSpan, 后续均设为 0 , 后修改到 onCell 钩子函数中实现
        // 想着列搜索时，动态设置 rowSpan, 后续还是不行，搜索可能会多一行，也可能会少一行，还是不能准确得到分类所占行数
        // 搜索后在 onCell 钩子中处理，还是有问题，又还原
        let n = 0
        _projectInfo.forEach(project => {
            // project.techClassRowNum = classArr[project.techClassId]
            // 按分类把第一条设好 rowSpan, 后续均设为 0
            if (n === 0) {
                project.techClassRowNum = classArr[project.techClassId as number]
            } else {
                project.techClassRowNum = 0
            }

            if (n === classArr[project.techClassId as number] - 1) {
                n = 0
            } else {
                n++
            }
        })

        setDataSource(_projectInfo)
        setTableLoading(false)
    }

    // 添加年的列
    const getYearColumns = (minYear: number, maxYear: number) => {
        const years = []
        const _dataIndexs = []
        for (let year = minYear; year <= maxYear; year++) {
            const _year = String(year)
            const _obj: IColProperty = {
                title: _year + (i18n.language !== 'zh' ? ' ' : '') + t('year'),
                className: 'year_col',
                children: []
            }
            const children: childrenType[] = []
            for (let i = 0; i < 4; i++) {
                const dataIndex = `${_year.substring(2)}-Q${i + 1}`
                _dataIndexs.push(dataIndex)
                children.push({
                    title: `Q${i + 1}`,
                    dataIndex,
                    key: `${_year.substring(2)}-Q${i + 1}`,
                    className: 'year_col',
                    ellipsis: { showTitle: true },
                    width: 100,
                    editable: false,
                    render: (text: number) => {
                        // return <div className='edit_txt'>{text || ''}</div>
                        return <div className={text ? (text === 1 ? 'plan_bg' : 'on_bg') : 'empty_bg'}>
                            {text ? (text === 1 ? 'PLAN' : 'ON') : ''}
                        </div>
                    },
                })
            }
            _obj.children = children
            years.push(_obj)
        }
        return {
            years,
            yearDataIndexs: _dataIndexs
        }
    }

    // 分页、排序、筛选变化时触发
    const handleChange: TableProps<any>['onChange'] = (pagination, filters) => {
        setFilteredInfo(filters)
    }
    const handleRow: TableProps<any>['onRow'] = (record: IProjectInfo) => {
        return {
            onDoubleClick: event => {
                console.log('handleRow: ', record, event)
                if (authInfo && Reflect.has(authInfo, 'viewDetailProjectInfo')) {
                    autoClick(`/projectInfoView?workGroupId=${locationSearch.current.workGroupId}&projectId=${record.id}&isView=true`)
                }
            },
        }
    }
    const handleSearch = (
        selectedKeys: string[],
        // eslint-disable-next-line no-unused-vars
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm()
        setSearchText(selectedKeys[0])
        setSearchedColumn(dataIndex)
    }
    const handleReset = (clearFilters: () => void) => {
        clearFilters()
        setSearchText('')
    }

    // 获取所有分类
    const getClassAll = async (workGroupId: number) => {
        const data = await getTechClassificationListAll(workGroupId)
        setClassification(data)
    }
    // 获取 product list
    const getProductList = async () => {
        const res = await getProdList({
            pageNo: 1,
            pageSize: 10000
        })
        setProdList(res.content)
    }
    // // 获取 bu list
    const getBuListFun = async () => {
        const res = await getBUList()
        setBuList(res)
    }

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    }
    const [routes, setRoutes] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/projectInfo',
            breadcrumbName: 'Services and Application',
        },
    ])
    const upperCaseFirst = (str: string) => {
        const letterArr = str.split('')
        // letterArr.splice(0, 1, letterArr[0].toUpperCase())
        if (letterArr.indexOf('-') !== -1) {
            return letterArr.join('')
        }
        const letters = letterArr.join('')
        switch (letters) {
            case 'operation':
                return t('Action')
            case 'classificationName':
                return t('techClass')
            case 'techPtn':
                return t('PTN')
            case 'techName':
                return t('Tech Name')
            case 'tmtapproval':
                return t('tmtApproval')
            case 'deliveryReview':
                return t('techDeliveryReview')
            case 'platforms':
                return t('productPlatform')
            case 'relevanceBuss':
                return t('buStakeholder')
            case 'dutyBuss':
                return t('developDutySubject')
            case 'techMaturity':
                return t('maturityStar')
        }
        return t(letters)
    }
    // 筛选列下拉选项
    const getFilterOptions = () => dataIndexs.map((dataIndex: string, index) => (
        <Option key={dataIndex} value={dataIndex} title={upperCaseFirst(dataIndex)}>
            <Checkbox checked={filterArr.includes(dataIndex)}
                disabled={index < 5 || index === allFilter.current.length - 1}
                className='select_col_option'
                onChange={(e: CheckboxChangeEvent) => {
                    const _arr: string[] = [...filterArr]
                    if (e.target.checked) {
                        _arr.push(dataIndex)
                    } else {
                        const findIndex = _arr.findIndex(item => item === dataIndex)
                        _arr.splice(findIndex, 1)
                    }
                    setAllCheck(_arr.length === allFilter.current.length)
                    setFilterArr(_arr)
                }}
            >{upperCaseFirst(dataIndex)}</Checkbox>
        </Option>
    ))

    // 自定义搜索
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<IProjectInfo> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        icon={<SearchOutlined />}
                        onClick={() => {
                            confirm()
                            setSearchText((selectedKeys as string[])[0])
                            setSearchedColumn(dataIndex)
                        }}
                    >
                        Search
                    </Button>
                </Space>
            </div>
        ),
        onFilterDropdownVisibleChange: visible => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100)
            }
        },
        render: text =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    })

    const isEmptyHtml = (htmlString: string) => {
        // 替换 &nbsp; 为普通空格，移除 HTML 标签，清理所有空白字符
        const text = htmlString
            .replace(/&nbsp;/g, ' ') // 替换 &nbsp; 为普通空格
            .replace(/<[^>]+>/g, '') // 移除 HTML 标签
            .replace(/\s+/g, '') // 移除所有空白字符（空格、制表符、换行符）
            .trim() // 确保去除首尾空格
        return text.length === 0
    }

    const defaultColumns: (ColumnTypes[number] & IColProperty)[] = [
        {
            title: () => <span>{t('Classification')}</span>,
            dataIndex: 'classificationName',
            key: 'classification',
            width: 150,
            ellipsis: { showTitle: true },
            fixed: true,
        },
        {
            title: () => <span>{t('PTN')}</span>,
            dataIndex: 'techPtn',
            key: 'techPtn',
            width: 150,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            fixed: true,
            // filterIcon: (filtered: boolean) => (
            //     <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            // ),
            // onFilter: (value, record) => {
            //     const result = record['techPtn']
            //         .toString()
            //         .toLowerCase()
            //         .includes((value as string).toLowerCase())
            //     return result
            // },
            render: wrapText
        },
        {
            title: () => <span>{t('researchForms')}</span>,
            dataIndex: 'researchForms',
            key: 'researchForms',
            width: 150,
            className: 'search_col lang_column',
            ellipsis: { showTitle: true },
            // filters: dicClassList['research form']
            //     ? dicClassList['research form'].map(item =>
            //         ({ text: item.name, value: item.value }))
            //     : [],
            // onFilter: (value, record) => {
            //     searchColumnCont.current = {
            //         value,
            //         fieldname: 'researchForms',
            //         record
            //     }
            //     // 重新设置 rowSpan
            //     rowClassObj.current = null
            //     const filedValue = record['researchForms']
            //     const result = filedValue ? filedValue.includes(value) : false
            //     return result
            // }
        },
        {
            title: () => <span>{t('Technology Name')}</span>,
            dataIndex: 'techName',
            key: 'technologyName',
            width: 180,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            // render: wrapText
            render(value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <Popover
                        title=""
                        content={
                            <div dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}></div>
                        }
                    >
                        <div style={{ maxWidth: 180 }}
                            className='table_ellipsis' dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}
                        ></div>
                    </Popover>
                } else {
                    return <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {wrapText(value)}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('subsidiary')}</span>,
            dataIndex: 'subsidiary',
            key: 'subsidiary',
            width: 150,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            render(value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <div
                        dangerouslySetInnerHTML={{
                            __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                        }}
                    ></div>
                } else {
                    return <Popover
                        title=""
                        content={
                            <div style={{ maxWidth: 240, maxHeight: 300, overflowY: 'auto', wordBreak: 'break-all' }}
                                dangerouslySetInnerHTML={{
                                    __html: value
                                }}
                            ></div>
                        }
                    >
                        <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {wrapText(value)}
                        </div>
                    </Popover>
                }
            }
        },
        {
            title: () => <span>{t('description')}</span>,
            dataIndex: 'description',
            width: 280,
            ellipsis: { showTitle: true },
            key: 'description',
            render(value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    const str = JSON.parse(value).str
                    const interceptStr = str.length > 60 ? str.substring(0, 60) + '...' : str
                    return <Popover
                        placement="topLeft"
                        title=""
                        content={
                            <div style={{ maxWidth: 240, maxHeight: 300, overflowY: 'auto', wordBreak: 'break-all' }}
                                dangerouslySetInnerHTML={{
                                    __html: (value.includes('"str":') && value.includes('"obj":')) ? str : value
                                }}
                            ></div>
                        }
                    >
                        <div
                            className='table_ellipsis2'
                            dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? interceptStr : value
                            }}
                        ></div>
                    </Popover>
                } else {
                    return <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {wrapText(value)}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('techLevel')}</span>,
            dataIndex: 'techLevel',
            key: 'techLevel',
            width: 100,
            className: 'search_col lang_column',
            ellipsis: { showTitle: true },
            render(value) {
                return [t('core'), t('key'), t('general'), t('normal')][value - 1]
            }
            // filters: [
            //     { text: 'YES', value: 'YES' },
            //     { text: 'No', value: 'No' },
            // ],
            // onFilter: (value, record) => {
            //     const filedValue = record['keyTech']
            //     const result = filedValue ? filedValue.includes(value) : false
            //     return result
            // }
        },
        {
            title: () => <span>{t('priority')}</span>,
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            className: 'lang_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('quantitativeIndicators')}</span>,
            dataIndex: 'quantitativeIndicators',
            key: 'quantitativeIndicators',
            width: 280,
            ellipsis: { showTitle: true },
            render(value) {
                const str = (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                console.log(str, 'str')
                console.log(isEmptyHtml(str), 'str')
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    if (!isEmptyHtml(str)) {
                        return <Popover
                            placement="topLeft"
                            title=""
                            content={
                                <div dangerouslySetInnerHTML={{
                                    __html: str
                                }}></div>
                            }
                        >
                            <div style={{ maxWidth: 240 }}
                                className='table_ellipsis'
                                dangerouslySetInnerHTML={{
                                    __html: str
                                }}
                            ></div>
                        </Popover>
                    } else {
                        return <div style={{ maxWidth: 240 }}
                            className='table_ellipsis'
                            dangerouslySetInnerHTML={{
                                __html: str
                            }}
                        ></div>
                    }

                } else {
                    return <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {wrapText(value)}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('tmtApproval')}</span>,
            dataIndex: 'tmtapproval',
            key: 'TMTApproval',
            width: 200,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('almCode')}</span>,
            dataIndex: 'almCode',
            key: 'almCode',
            width: 100,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('techDeliveryReview')}</span>,
            dataIndex: 'deliveryReview',
            key: 'deliveryReview',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('maturity')}</span>,
            dataIndex: 'maturity',
            key: 'maturity',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('isMajorProject')}</span>,
            dataIndex: 'isMajorProject',
            key: 'isMajorProject',
            width: 100,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: boolean) {
                return val ? (<span>{t('Yes')}</span>) : (<span>{t('No')}</span>)
            }
        },
        {
            title: () => <span>{t('maturityStar')}</span>,
            dataIndex: 'techMaturity',
            key: 'maturityDefine',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: string) {
                if (dicClassList['技术成熟度']) {
                    const findObj = dicClassList['技术成熟度'].find(item => item.value === val)
                    if (findObj) {
                        return findObj.name
                    }
                }
                return ''
            }
        },
        {
            title: () => <span>{t('requirementSource')}</span>,
            dataIndex: 'requirementSource',
            key: 'requirementSource',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('developedBy')}</span>,
            dataIndex: 'developedBy',
            key: 'developedBy',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('product') + (i18n.language !== 'zh' ? ' ' : '') + t('platform')}</span>,
            dataIndex: 'platforms',
            key: 'platforms',
            width: 200,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: any[]) {
                const len = val.length
                return val.map((item: any, index) => (<span key={item.id}>
                    <span>{item.productPlatform}</span>
                    {(index < len - 1) && <span>, </span>}
                </span>))
            }
        },
        {
            title: () => <span>{t('buStakeholder')}</span>,
            dataIndex: 'relevanceBuss',
            key: 'relevanceBuss',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: any[]) {
                const len = val.length
                return val.map((item: any, index) => (<span key={item.id}>
                    <span>{item.bu}</span>
                    {(index < len - 1) && <span>, </span>}
                </span>))
            }
        },
        {
            title: () => <span>{t('developDutySubject')}</span>,
            dataIndex: 'dutyBuss',
            key: 'dutyBuss',
            width: 200,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: any[]) {
                const len = val.length
                return val.map((item: any, index) => (<span key={item.id}>
                    <span>{item.bu}</span>
                    {(index < len - 1) && <span>, </span>}
                </span>))
            }
        },
        {
            title: () => <span>{t('techPlanCost')}</span>,
            dataIndex: 'planBudgeting',
            key: 'planBudgeting',
            width: 250,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: number) {
                if (val === null) {
                    return ''
                }
                if (isNaN(Number(val))) {
                    val = 0
                }
                val = Math.round(val)
                const result = val.toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')
                return (<span>{result}</span>)
            }
        },
        {
            title: () => <span>{t('resourceNeeded')}</span>,
            dataIndex: 'resourceNeeded',
            key: 'resourceNeeded',
            width: 200,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('budgeting')}</span>,
            dataIndex: 'budgeting',
            key: 'budgeting',
            width: 100,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(val: number) {
                if (val === null) {
                    return ''
                }
                if (isNaN(Number(val))) {
                    val = 0
                }
                val = Math.round(val)
                const result = val.toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')
                return (<span>{result}</span>)
            }
        },
        {
            title: () => <span>{t('toolsEquipment')}</span>,
            dataIndex: 'toolsEquipment',
            key: 'toolsEquipment',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('projectName')}</span>,
            dataIndex: 'projectName',
            key: 'projectName',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.projectName
            }
        },
        {
            title: () => <span>{t('approvalDate')}</span>,
            dataIndex: 'approvalDate',
            key: 'approvalDate',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.approvalDate
            }
        },
        {
            title: () => <span>{t('estimateClosedDate')}</span>,
            dataIndex: 'estimateClosedDate',
            key: 'estimateClosedDate',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.estimateClosedDate
            }
        },
        {
            title: () => <span>{t('actualClosedDate')}</span>,
            dataIndex: 'actualClosedDate',
            key: 'actualClosedDate',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.actualClosedDate
            }
        },
        {
            title: () => <span>{t('projectResult')}</span>,
            dataIndex: 'projectResult',
            key: 'projectResult',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.project?.projectResult
            }
        },
        {
            title: () => <span>{t('patentCode')}</span>,
            dataIndex: 'patentCode',
            key: 'patentCode',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
            render(_: any, record: any) {
                return record.patent?.patentCode
            }
        },
        {
            title: () => <span>{t('Action')}</span>,
            dataIndex: 'operation',
            key: 'operation',
            width: 150,
            fixed: 'right',
            render: viewButton
        }
    ]
    // 获取 columns
    // 初始根据 columns 获取 dataIndexs
    // filterCond 为 false，取全部，不用过滤
    const getFilterInfo = (filterCond: boolean) => {
        // 获取年
        const _dataIndexs: string[] = []
        const filterFn = (arr: any) => arr.filter((item: any) => {
            if (item.children) {
                item.children = filterFn(item.children)
                return true
            }
            return filterArr.includes(item.dataIndex)
        })

        const _arr = defaultColumns.map(col => {
            if (col.dataIndex) {
                _dataIndexs.push(col.dataIndex)
            }
            if (!col.editable) {
                // 判断第一列设置占行数
                if (col.dataIndex === 'classificationName') {
                    return {
                        ...col,
                        // filteredValue: filteredInfo[col.dataIndex] || null,
                        // onCell: (record: IProjectInfo) => {
                        //     return { rowSpan: record.techClassRowNum }
                        //     // rowClassObj.current 的值清空后，还是会自动刷新 table 而又有值，导致 colSpan 又不准确
                        //     // if (!rowClassObj.current) {
                        //     //     // 第一次还没有赋过值
                        //     //     rowClassObj.current = {
                        //     //         [record.techClassId]: 0
                        //     //     }
                        //     //     return { rowSpan: record.techClassRowNum } // 通过数据计算所占行数
                        //     // } else {
                        //     //     if (!(record.techClassId in rowClassObj.current)) {
                        //     //         // 当前分类第一行设置 rowSpan 为 techClassRowNum
                        //     //         rowClassObj.current = {
                        //     //             [record.techClassId]: 0
                        //     //         }
                        //     //         return { rowSpan: record.techClassRowNum } // 通过数据计算所占行数
                        //     //     } else {
                        //     //         // 如果当前分类还没显示完，则自增, 否则清零进入下一个分类
                        //     //         if (rowClassObj.current[record.techClassId] < (record.techClassRowNum || 0)) {
                        //     //             rowClassObj.current[record.techClassId]++
                        //     //         }
                        //     //         return { rowSpan: 0 } // 通过数据计算所占行数
                        //     //     }
                        //     // }
                        // }
                    }
                } else if (col.children) {
                    _dataIndexs.push(
                        ...col.children.map(chd => chd.dataIndex)
                    )
                    // 多行表头设置子级
                    return {
                        ...col,
                        children: col.children.map(chd => ({
                            ...chd,
                            // filteredValue: filteredInfo[chd.dataIndex] || null,
                            onCell: (record: IProjectInfo) => ({
                                record,
                                editable: chd.editable,
                                dataIndex: chd.dataIndex,
                                title: chd.title,
                            }),
                        }))
                    }
                    // 技术编号列自定义搜索
                } else if (col.dataIndex === 'techPtn') {
                    return {
                        ...col,
                        // ...getColumnSearchProps(col.dataIndex),
                        className: 'search_col',
                        // filteredValue: filteredInfo[col.dataIndex] || null,
                    }
                } else {
                    return {
                        ...col,
                        // filteredValue: (col.dataIndex && filteredInfo[col.dataIndex]) || null,
                    }
                }
            }
            return {
                ...col,
                // filteredValue: (col.dataIndex && filteredInfo[col.dataIndex]) || null,
                onCell: (record: IProjectInfo) => ({
                    record,
                    editable: col.editable,
                    dataIndex: col.dataIndex,
                    title: col.title,
                }),
            }
        })
        // 取出最低年-最高年 增加 年 列数
        let minYear = 9999, maxYear = 0
        projectInfo.forEach(project => {
            project.techYearQuarters.forEach(item => {
                minYear = Math.min(item.year, minYear)
                maxYear = Math.max(item.year, maxYear)
            })
        })
        const { years, yearDataIndexs }: { years: any[], yearDataIndexs: string[] } = getYearColumns(minYear, maxYear)
        _arr.splice(8, 0, ...years)
        _dataIndexs.splice(8, 0, ...yearDataIndexs)
        return {
            columnArr: filterCond ? filterFn(_arr) : _arr,
            dataIndexArr: _dataIndexs
        }
    }

    // 分页
    const onPaginationChange: PaginationProps['onChange'] = (pageNumber: number): void => {
        if (pageSizeChanged.current) return
        dispatch(getProjectInfoList({
            ...searchObj,
            pageNo: pageNumber,
            pageSize: paginationInfo.pageSize,
            workGroupId: curWorkgroup?.id
        }))
        dispatch(setProjectInfoPagination({
            pageIndex: pageNumber
        }))
    }
    const onPageSizeChange = (current: number, size: number) => {
        // 避免重复触发 pageChange 事件
        pageSizeChanged.current = true
        dispatch(getProjectInfoList({
            ...searchObj,
            pageNo: 1,
            pageSize: size,
            workGroupId: curWorkgroup?.id
        }))
        dispatch(setProjectInfoPagination({
            pageIndex: 1,
            pageSize: size
        }))
        setTimeout(() => {
            pageSizeChanged.current = false
        }, 100)
    }
    useEffect(() => {
        console.log('history.location: ', history.location, locationObj, route, workgroupList)
        setTableKey(getUuid(8))

        if (workgroupList.length === 0) {
            dispatch(getWorkgroup())
        }

        const routeParams = new URLSearchParams(location.search)
        // 邮件跳转过来设置的参数名
        const worGroupIdParam = routeParams.get('workGroupId')
        const yearParam = routeParams.get('year')
        const priorityParam = routeParams.get('priority')
        const planStatusParam = routeParams.get('planStatus')
        const techLevelParam = routeParams.get('techLevel')
        const maturityParam = routeParams.get('maturity')
        const _projectIdParam = routeParams.get('projectId')
        console.log('searchParams: ', worGroupIdParam, yearParam, planStatusParam, techLevelParam, maturityParam)
        locationSearch.current = {
            workGroupId: +(worGroupIdParam || '')
        }

        if (_projectIdParam) {
            setProjectIdParam(_projectIdParam)
        }
        // 从邮件跳转过来
        if (worGroupIdParam) {
            setSearchObj({
                isAll: true,
                year: yearParam ? [yearParam] : undefined,
                priority: priorityParam ? [priorityParam] : undefined,
                status: planStatusParam === 'Plan' ? 1 : (planStatusParam === 'On' ? 2 : undefined),
                techLevel: techLevelParam === 'core' ? [1] : (techLevelParam === 'key' ? [2] : undefined),
                maturity: maturityParam ? [maturityParam] : undefined,
                pageNo: paginationInfo.pageIndex,
                pageSize: paginationInfo.pageSize
            })
            setCurWorkgroup(workgroupList.find(item => item.id === +worGroupIdParam))
            // } else {
            // const { state } = locationObj as { state: { id: number } | undefined }
            // if (state?.id) {
            //     // 从首页跳转过来
            //     setCurWorkgroup(workgroupList.find(item => item.id === state.id))
            // } else {
            //     // 从编辑页返回时，参数从 store route 中获取
            //     const routeState = route.location.state as {id: number}
            //     if (routeState) {
            //         setCurWorkgroup(workgroupList.find(item => item.id === routeState?.id))
            //     }
            // }
            // history.replace('/indexPage')
        }
        setInit(false)
        // 获取 product list
        getProductList()
        // // 获取 BU list
        getBuListFun()

        // 获取字典列表，作为下拉选项
        if (Object.keys(dicClassList).length === 0) {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
            getDicClass(dispatch, (a: boolean): void => { })
        }

        setWinh(document.body.clientHeight - 365)
        window.onresize = () => {
            setWinh(document.body.clientHeight - 365)
        }
        return () => {
            window.onresize = null
        }
    }, [])
    useEffect(() => {
        if (workgroupList.length) {
            if (locationSearch.current.workGroupId) {
                setCurWorkgroup(workgroupList.find(item => item.id === +locationSearch.current.workGroupId))
            }
        }
    }, [workgroupList])
    // 设置当前workgroup
    useEffect(() => {
        const _routes = [...routes]
        if (curWorkgroup && _routes.length > 1) {
            _routes[_routes.length - 1].breadcrumbName = curWorkgroup?.groupName
            setRoutes(_routes)
        }
        if (curWorkgroup) {
            // 获取表格列表
            console.log('setSearchObj: ', searchObj)
            dispatch(setProjectInfoPagination({
                pageIndex: 1,
                pageSize: paginationInfo.pageSize
            }))
            dispatch(getProjectInfoList({
                ...searchObj,
                techClassId: '',
                workGroupId: curWorkgroup?.id,
                pageNo: 1,
                pageSize: paginationInfo.pageSize
            }))
            // 获取 classificationList
            // dispatch(getTechClassification({
            //     workGroupId: curWorkgroup?.id
            // }))
            getClassAll(curWorkgroup.id)
        }
    }, [curWorkgroup])
    // 从表格数据中剔重得出类型列表
    useEffect(() => {
        const _classification = [...classification]
        const groupByCategory = _classification.reduce((group: any, product) => {
            let { classId } = product
            if (classId === undefined && Reflect.has(product, 'id')) {
                product.classId = product.id
                classId = product.classId
            }
            group[classId] = group[classId] || []
            group[classId].push(product)
            return group
        }, {})
        const _classArr: any[] = []
        Object.keys(groupByCategory).forEach(key => {
            _classArr.push(groupByCategory[key][0])
        })
        setClassArr([..._classArr])
    }, [classification])
    // 用于过滤的字段名
    useEffect(() => {
        if (init) return
        // 获取 columns
        // 初始根据 columns 获取 dataIndexs
        const { columnArr, dataIndexArr }: { columnArr: any[], dataIndexArr: string[] } = getFilterInfo(filterArr.length > 0)
        // 如果一年中所有季度都过滤掉了，则不显示该年
        const columnFilterResult = columnArr.filter(item => {
            return !(typeof item.title === 'string' && item.title.includes('20') && item.children.length === 0)
        })
        setColumns(columnFilterResult)
        setDataIndexs(dataIndexArr)
        if (filterArr.length) {
            localStorage.setItem('filterArr', filterArr.join(','))
            localStorage.setItem('allLen', '' + allFilter.current.length)
        }
        // 设置数据源
        // 分类所占行数
        // setSourceRows()
        // 本意是在前端过滤，但因为分页的原因，前端不能拿到完整列表，过滤后的total就会不一致
        // (看是后端增加参数还是怎么处理)
        // if (searchObj.planStatus) {
        //     const filterArr = projectInfo.filter(item => {
        //         const findObj = item.techYearQuarters.find(subItem => subItem.status === searchObj.planStatus)
        //         return !!findObj
        //     })
        //     console.log('filterArr: ', filterArr)
        //     setDataSource(filterArr)
        //     // setDataTotal(filterArr.length)
        // } else {
        setDataSource([...projectInfo])
        // }
        console.log('paginationInfo: ', JSON.parse(JSON.stringify(paginationInfo)))
        // setDataTotal(paginationInfo?.total || 0)
        setTableLoading(false)
    }, [filterArr, filteredInfo, projectInfo])
    // 修改数据源时更新 filter column options
    useEffect(() => {
        if (init) return
        // 获取 columns
        // 初始根据 columns 获取 dataIndexs
        let dataIndexArr: string[] = []
        const _filterArr = localStorage.getItem('filterArr')
        const allLen = localStorage.getItem('allLen')
        const result = getFilterInfo(false)
        allFilter.current = result.dataIndexArr
        if (!_filterArr && filterArr.length === 0) {
            dataIndexArr = result.dataIndexArr
        } else {
            dataIndexArr = _filterArr?.split(',') as string[]
            setAllCheck(dataIndexArr.length === +(allLen as string))
        }
        setFilterArr(dataIndexArr)
    }, [projectInfo])
    // 过滤显示的字段
    useEffect(() => {
        // 字段列表
        // 获取 options
        const _filterOptions = getFilterOptions()
        setFilterOptions(_filterOptions)
    }, [dataIndexs])
    useEffect(() => {
        if (projectIdParam) {
            setTimeout(() => {
                let curRow = document.getElementsByClassName('cur_view_row')[0] as HTMLElement
                console.log('curRow: ', curRow)
                if (curRow) {
                    while (!(hasClass(curRow, 'ant-table-body'))) {
                        curRow = curRow.parentNode as HTMLElement
                    }
                    // const posOfParent = GetObjPos(curRow, 'ant-table-body')
                    const posOfParent = curRow.offsetTop
                    console.log('pos: ', posOfParent)
                    console.log('tbody', curRow)
                    curRow.scrollTo(0, posOfParent)
                    // curRow.style.transform = 'translateY(-100px)'
                }
            }, 1000)
        }
    }, [projectIdParam])
    return (
        <DataSourceContext.Provider value={{
            dataSource,
            setDataSource
        }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            <Space direction="vertical" size={0} style={{ display: 'flex' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PageHeader className='chart_title'
                        onBack={() => null}
                        title={routes[routes.length - 1].breadcrumbName}
                    />
                    <Space>
                        {/* checkbox 多选触发渲染 多选会换行，此处不用原样式 */}
                        <Select defaultValue={['']} value={['']} style={{ width: 180 }}
                            mode="multiple" className='select_col'
                        >
                            <Option value='' className='select_col_filter'>{t('filterColumn')} <DownOutlined /></Option>
                            <Option value='all' className='select_col_option'><Checkbox checked={allCheck}
                                onChange={(e: CheckboxChangeEvent) => {
                                    if (e.target.checked) {
                                        setFilterArr(allFilter.current)
                                    } else {
                                        const _filterArr = allFilter.current.slice(0, 5)
                                        _filterArr.push(allFilter.current[allFilter.current.length - 1])
                                        setFilterArr(_filterArr)
                                    }
                                    setAllCheck(e.target.checked)
                                }}
                            >{t('All')}</Checkbox></Option>
                            {filterOptions}
                        </Select>
                        <Button type='primary'
                            icon={<SearchOutlined />}
                            onClick={() => {
                                setShowSearchPop(!showSearchPop)
                            }}
                        >{t('search')}</Button>
                        {authInfo && Reflect.has(authInfo, 'addProjectInfo') && <Button type='primary'
                            icon={<PlusOutlined />}
                            onClick={() => {
                                // 清空表单数据
                                dispatch(clearEdit2Data())
                                history.push('/projectInfoAdd?workGroupId=' + locationSearch.current.workGroupId)
                            }}
                        >{t('add')}</Button>}
                        {authInfo && Reflect.has(authInfo, 'manageClass') && <Button type='primary'
                            icon={<NodeExpandOutlined />}
                            onClick={() => {
                                dispatch(setTechClassification([]))
                                dispatch(setClassificationPagination({
                                    pageIndex: 1
                                }))
                                history.push('/classificationAdd?workGroupId=' + locationSearch.current.workGroupId)
                            }}
                        >{t('Class Manage')}</Button>}
                        {authInfo && Reflect.has(authInfo, 'exportExcel') && <Button type='primary'
                            icon={<CloudDownloadOutlined />}
                            onClick={async () => {
                                const res = await exportExcel({ workGroupId: locationSearch.current.workGroupId })
                                downloadFile(res, (curWorkgroup?.groupName || 'workgroup') + '.ProjectInfo.' + res.suffix)
                            }}
                        >{t('export')}</Button>}
                    </Space>
                </div>
                <ConfigProvider locale={i18n.language === 'zh' ? zhCN : undefined}>
                    <ResizableTable className='index_ls tech_table' bordered
                        key={tableKey}
                        cols={columns as ColumnTypes}
                        tableData={dataSource}
                        components={components}
                        childrenColumnName='xxx'
                        tableLayout='fixed'
                        rowKey="id"
                        rowClassName={(record: any) => {
                            let curRowClass = ''
                            if (projectIdParam && (record.id === +projectIdParam)) {
                                curRowClass = 'cur_view_row'
                            }
                            return `${record.className || ''} ${curRowClass} editable-row`
                        }}
                        scroll={{ x: 'max-content', y: winH }}
                        onChange={handleChange}
                        onRow={handleRow}
                        loading={tableLoading}
                        pagination={{
                            current: paginationInfo.pageIndex,
                            pageSize: paginationInfo.pageSize,
                            total: paginationInfo.total,
                            showQuickJumper: true,
                            onChange: onPaginationChange,
                            showSizeChanger: true,
                            onShowSizeChange: onPageSizeChange,
                            showTotal: (total: number) =>
                                `${t('total')} ${total} ${t('items')}`,
                            style: { marginBottom: 13 }
                        }}
                    />
                </ConfigProvider>
            </Space>
            <DrawerShowContext.Provider value={{
                showSearchPop,
                setShowSearchPop,
                searchObj,
                setSearchObj,
                classArr,
                curWorkgroup,
                prodList,
                buList
            }}>
                <Table2SearchPop />
            </DrawerShowContext.Provider>
            <LogListPop recordId={viewLogId} tableParam='tech_project_info' setViewLogId={setViewLogId} />
            <HistoryListPop recordId={viewHistoryId} setViewHistoryId={setViewHistoryId} />
            <TechGraphPop recordId={viewTechGraphId} setViewTechGraphId={setViewTechGraphId} workGroupId={locationSearch.current.workGroupId} />
        </DataSourceContext.Provider>
    )
}

export default TablePage2
